import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Table2, ChevronDown, ChevronUp, X, FileText, Paperclip, Calculator, Save } from 'lucide-react';
import CarbonCalculator from './CarbonCalculator';
import RegisterToSaveModal from './RegisterToSaveModal';
import api from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = {
  general: { label: 'General & DMA', color: 'emerald' },
  environmental: { label: 'Environmental', color: 'green' },
  social: { label: 'Social', color: 'blue' },
  governance: { label: 'Governance', color: 'purple' }
};

const QuestionnaireStep = ({ assessmentId, definitionId, initialAssessment, color: colorKey = 'emerald', onNext }) => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [activeCategory, setActiveCategory] = useState('general');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [ghgData, setGhgData] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [expandedTables, setExpandedTables] = useState({});
  const [dataTables, setDataTables] = useState({});
  const [comments, setComments] = useState({}); // { [questionId]: string }
  const [validationErrors, setValidationErrors] = useState({}); // { [questionId]: string }
  const [showRegisterModal, setShowRegisterModal] = useState(false);

  const isAuthenticated = Boolean(typeof window !== 'undefined' && localStorage.getItem('auth_token'));

  // Resolve color palette (matches ASSESSMENT_COLORS from AssessmentWizard).
  const palettes = {
    emerald: { bg: 'bg-emerald-600', bgHover: 'hover:bg-emerald-700', text: 'text-emerald-600', textHover: 'hover:text-emerald-700', ring: 'focus:ring-emerald-500', border: 'border-emerald-500', bgSoft: 'bg-emerald-50', progressBg: 'bg-emerald-500', borderSoft: 'border-emerald-300', tabActive: 'bg-emerald-600 text-white', tabCount: 'bg-emerald-500', btnPrimary: 'bg-emerald-600 hover:bg-emerald-700', optionSelected: 'bg-emerald-600 border-emerald-500 text-white', softBorder: 'border-emerald-100', softBg: 'bg-emerald-50/50' },
    blue:    { bg: 'bg-blue-600',    bgHover: 'hover:bg-blue-700',    text: 'text-blue-600',    textHover: 'hover:text-blue-700',    ring: 'focus:ring-blue-500',    border: 'border-blue-500',    bgSoft: 'bg-blue-50',    progressBg: 'bg-blue-500',    borderSoft: 'border-blue-300',    tabActive: 'bg-blue-600 text-white',    tabCount: 'bg-blue-500',    btnPrimary: 'bg-blue-600 hover:bg-blue-700',    optionSelected: 'bg-blue-600 border-blue-500 text-white',    softBorder: 'border-blue-100',    softBg: 'bg-blue-50/50' },
    purple:  { bg: 'bg-purple-600',  bgHover: 'hover:bg-purple-700',  text: 'text-purple-600',  textHover: 'hover:text-purple-700',  ring: 'focus:ring-purple-500',  border: 'border-purple-500',  bgSoft: 'bg-purple-50',  progressBg: 'bg-purple-500',  borderSoft: 'border-purple-300',  tabActive: 'bg-purple-600 text-white',  tabCount: 'bg-purple-500',  btnPrimary: 'bg-purple-600 hover:bg-purple-700',  optionSelected: 'bg-purple-600 border-purple-500 text-white',  softBorder: 'border-purple-100',  softBg: 'bg-purple-50/50' },
    amber:   { bg: 'bg-amber-600',   bgHover: 'hover:bg-amber-700',   text: 'text-amber-600',   textHover: 'hover:text-amber-700',   ring: 'focus:ring-amber-500',   border: 'border-amber-500',   bgSoft: 'bg-amber-50',   progressBg: 'bg-amber-500',   borderSoft: 'border-amber-300',   tabActive: 'bg-amber-600 text-white',   tabCount: 'bg-amber-500',   btnPrimary: 'bg-amber-600 hover:bg-amber-700',   optionSelected: 'bg-amber-600 border-amber-500 text-white',   softBorder: 'border-amber-100',   softBg: 'bg-amber-50/50' },
    rose:    { bg: 'bg-rose-600',    bgHover: 'hover:bg-rose-700',    text: 'text-rose-600',    textHover: 'hover:text-rose-700',    ring: 'focus:ring-rose-500',    border: 'border-rose-500',    bgSoft: 'bg-rose-50',    progressBg: 'bg-rose-500',    borderSoft: 'border-rose-300',    tabActive: 'bg-rose-600 text-white',    tabCount: 'bg-rose-500',    btnPrimary: 'bg-rose-600 hover:bg-rose-700',    optionSelected: 'bg-rose-600 border-rose-500 text-white',    softBorder: 'border-rose-100',    softBg: 'bg-rose-50/50' },
  };
  const P = palettes[colorKey] || palettes.emerald;

  const [milestones, setMilestones] = useState([]);
  const [activeMilestone, setActiveMilestone] = useState(null);

  useEffect(() => {
    if (!definitionId) {
      // Backward compat: no assessment context — load all questions, show by category.
      fetch(`${API_URL}/api/questions`).then(r => r.json()).then(data => {
        setQuestions(data); setLoading(false);
      }).catch(() => setLoading(false));
      return;
    }
    // Primary: fetch milestones + questions scoped to this assessment
    fetch(`${API_URL}/api/client/assessments/${definitionId}/questions`).then(r => r.json()).then(data => {
      const ms = Array.isArray(data.milestones) ? data.milestones : [];
      const qs = Array.isArray(data.questions) ? data.questions : [];
      setMilestones(ms);
      setQuestions(qs);
      if (ms.length > 0 && activeMilestone == null) {
        setActiveMilestone(ms[0].id);
      }
      setLoading(false);
      // Load linked data tables (if any)
      const tableIds = new Set();
      for (const q of qs) {
        for (const tid of (q.linked_table_ids || [])) tableIds.add(tid);
      }
      for (const tid of tableIds) {
        fetch(`${API_URL}/api/data-tables/${tid}`).then(r => r.json()).then(t => {
          setDataTables(prev => ({ ...prev, [tid]: t }));
        }).catch(() => {});
      }
    }).catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definitionId]);

  // Hydrate from existing draft/assessment state if resuming
  useEffect(() => {
    if (!initialAssessment || !initialAssessment.answers) return;
    const aMap = {};
    const cMap = {};
    const fMap = {};
    const gMap = {};
    for (const a of initialAssessment.answers) {
      if (!a.question_id) continue;
      aMap[a.question_id] = { question_id: a.question_id, answer_option_id: a.answer_option_id };
      if (a.comment_text) cMap[a.question_id] = a.comment_text;
      if (a.ghg_data) gMap[a.question_id] = a.ghg_data;
      if (Array.isArray(a.uploaded_file_ids) && a.uploaded_file_ids.length > 0) {
        fMap[a.question_id] = a.uploaded_file_ids.map(id => ({ id, filename: 'File' }));
      }
    }
    setAnswers(aMap);
    setComments(cMap);
    setUploadedFiles(fMap);
    setGhgData(gMap);
  }, [initialAssessment]);

  // Group questions by milestone when we have milestones (multi-assessment mode).
  // Fall back to legacy category grouping otherwise.
  const useMilestoneMode = milestones.length > 0;

  const groupedByMilestone = useMemo(() => {
    const g = {};
    for (const q of questions) {
      const mid = q.milestone_id || 'unscoped';
      if (!g[mid]) g[mid] = [];
      g[mid].push(q);
    }
    return g;
  }, [questions]);

  const groupedByCategory = useMemo(() => {
    const g = {};
    for (const q of questions) {
      const cat = q.category || 'general';
      if (!g[cat]) g[cat] = [];
      g[cat].push(q);
    }
    return g;
  }, [questions]);

  // `grouped` unifies both modes so the rest of the component can stay the same.
  const grouped = useMilestoneMode ? groupedByMilestone : groupedByCategory;
  const categories = useMilestoneMode
    ? milestones.map(m => m.id).filter(id => (grouped[id] || []).length > 0)
    : Object.keys(CATEGORIES).filter(c => grouped[c]?.length > 0);

  // Active group key — initialise to first available.
  useEffect(() => {
    if (useMilestoneMode && activeMilestone == null && milestones.length > 0) {
      setActiveMilestone(milestones[0].id);
    }
  }, [useMilestoneMode, milestones, activeMilestone]);

  const activeGroupKey = useMilestoneMode ? (activeMilestone || categories[0]) : activeCategory;
  const setActiveGroupKey = useMilestoneMode ? setActiveMilestone : setActiveCategory;
  const currentQuestions = grouped[activeGroupKey] || [];

  const groupLabel = (key) => {
    if (useMilestoneMode) {
      const m = milestones.find(x => x.id === key);
      return m ? m.name : '—';
    }
    return CATEGORIES[key]?.label || key;
  };

  // Auto-advance to next group when current is complete
  const checkAndAdvanceCategory = useCallback((newAnswers) => {
    const currentGroupQs = grouped[activeGroupKey] || [];
    const answeredCount = currentGroupQs.filter(q => newAnswers[q.id]).length;

    if (answeredCount === currentGroupQs.length && currentGroupQs.length > 0) {
      const order = categories;
      const currentIdx = order.indexOf(activeGroupKey);
      for (let i = currentIdx + 1; i < order.length; i++) {
        const nextKey = order[i];
        const nextQs = grouped[nextKey] || [];
        if (nextQs.length > 0) {
          const nextAnswered = nextQs.filter(q => newAnswers[q.id]).length;
          if (nextAnswered < nextQs.length) {
            setTimeout(() => setActiveGroupKey(nextKey), 500);
            break;
          }
        }
      }
    }
  }, [activeGroupKey, grouped, categories, setActiveGroupKey]);

  const handleSelectAnswer = (questionId, optionId) => {
    const newAnswers = { ...answers, [questionId]: { question_id: questionId, answer_option_id: optionId } };
    setAnswers(newAnswers);
    checkAndAdvanceCategory(newAnswers);
  };

  const handleGhgChange = (questionId, scope, field, value) => {
    setGhgData(prev => ({
      ...prev,
      [questionId]: {
        ...(prev[questionId] || {}),
        [scope]: { ...(prev[questionId]?.[scope] || {}), [field]: value }
      }
    }));
  };

  const handleFileUpload = async (questionId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    const params = new URLSearchParams({ execution_id: assessmentId || '', question_id: questionId });
    try {
      const res = await fetch(`${API_URL}/api/upload?${params}`, { method: 'POST', body: formData });
      if (res.ok) {
        const data = await res.json();
        setUploadedFiles(prev => ({
          ...prev,
          [questionId]: [...(prev[questionId] || []), data]
        }));
      }
    } catch (err) { console.error(err); }
  };

  const removeFile = (questionId, fileId) => {
    setUploadedFiles(prev => ({
      ...prev,
      [questionId]: (prev[questionId] || []).filter(f => f.id !== fileId)
    }));
  };

  const answeredInCategory = (cat) => (grouped[cat] || []).filter(q => answers[q.id]).length;

  const handleSaveDraft = async () => {
    if (!assessmentId) return;
    // Anonymous users must register first so the draft can be linked to an account
    if (!isAuthenticated) {
      // Save the work first (without user attribution), then prompt register
      await persistDraft();
      setShowRegisterModal(true);
      return;
    }
    await persistDraft();
  };

  const persistDraft = async () => {
    setSavingDraft(true);
    try {
      const answerList = Object.values(answers).map(a => ({
        ...a,
        comment_text: comments[a.question_id] || "",
        ghg_data: ghgData[a.question_id] || null,
        uploaded_file_ids: (uploadedFiles[a.question_id] || []).map(f => f.id),
      }));
      const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
      const res = await fetch(`${API_URL}/api/assessments/${assessmentId}/save-draft`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ answers: answerList })
      });
      if (res.ok) {
        setDraftSavedAt(new Date());
      }
    } catch (err) { console.error(err); }
    setSavingDraft(false);
  };

  const handleSaveAndExit = async () => {
    if (!isAuthenticated) {
      await persistDraft();
      setShowRegisterModal(true);
      return;
    }
    await persistDraft();
    navigate('/dashboard');
  };

  const handleRegisterSaved = () => {
    setShowRegisterModal(false);
    // After registration the token is set & draft linked; navigate to dashboard
    navigate('/dashboard');
  };

  const handleSubmit = async () => {
    // Validate per-option required comments/documents
    const errors = {};
    for (const q of questions) {
      const ans = answers[q.id];
      if (!ans) continue;
      const opt = (q.answer_options || []).find(o => o.id === ans.answer_option_id);
      if (!opt) continue;
      if (opt.comment_mode === "required" && !(comments[q.id] || "").trim()) {
        errors[q.id] = "Please add a comment for the selected answer.";
        continue;
      }
      if (opt.document_mode === "required" && (uploadedFiles[q.id] || []).length === 0) {
        errors[q.id] = "Please upload a supporting document for the selected answer.";
      }
    }
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      // Jump to first error's category
      const firstQid = Object.keys(errors)[0];
      const firstQ = questions.find(q => q.id === firstQid);
      if (firstQ) {
        if (useMilestoneMode && firstQ.milestone_id) setActiveMilestone(firstQ.milestone_id);
        else setActiveCategory(firstQ.category || 'general');
      }
      return;
    }
    setValidationErrors({});
    setSubmitting(true);
    try {
      const answerList = Object.values(answers).map(a => ({
        ...a,
        comment_text: comments[a.question_id] || "",
        ghg_data: ghgData[a.question_id] || null,
        uploaded_file_ids: (uploadedFiles[a.question_id] || []).map(f => f.id),
      }));
      await fetch(`${API_URL}/api/assessments/${assessmentId}/answers`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers: answerList })
      });
      await fetch(`${API_URL}/api/assessments/${assessmentId}/complete`, { method: 'POST' });
      const result = await fetch(`${API_URL}/api/assessments/${assessmentId}`).then(r => r.json());
      onNext(result);
    } catch (err) { console.error(err); }
    setSubmitting(false);
  };

  const toggleTable = (qId) => setExpandedTables(prev => ({ ...prev, [qId]: !prev[qId] }));

  if (loading) return <div className="flex items-center justify-center py-20 text-gray-400">Loading questions...</div>;

  const totalAnswered = Object.keys(answers).length;
  const totalQuestions = questions.length;

  return (
    <div data-testid="questionnaire-step" className="max-w-5xl mx-auto">
      {/* Progress */}
      <div className="mb-6">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-gray-500">Progress</span>
          <span className="text-gray-900 font-medium">{totalAnswered}/{totalQuestions} answered</span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div className={`h-full ${P.progressBg} transition-all duration-300 rounded-full`} style={{ width: `${totalQuestions > 0 ? (totalAnswered / totalQuestions * 100) : 0}%` }} />
        </div>
      </div>

      {/* Group Tabs (milestones in multi-assessment mode, categories otherwise) */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {categories.map(key => {
          const count = grouped[key]?.length || 0;
          const answered = answeredInCategory(key);
          const isActive = activeGroupKey === key;
          return (
            <button key={key} onClick={() => setActiveGroupKey(key)}
              data-testid={useMilestoneMode ? `milestone-tab-${key}` : `category-tab-${key}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${isActive ? P.tabActive : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'}`}>
              {groupLabel(key)}
              <span className={`text-xs px-1.5 py-0.5 rounded ${answered === count ? 'bg-green-100 text-green-700' : isActive ? `${P.tabCount} text-white` : 'bg-gray-100 text-gray-500'}`}>{answered}/{count}</span>
            </button>
          );
        })}
      </div>

      {/* Questions */}
      <div className="space-y-4">
        {currentQuestions.map((q, idx) => {
          const opts = q.answer_options || [];
          const selectedAnswer = answers[q.id];
          const linkedTables = (q.linked_table_ids || []).map(tid => dataTables[tid]).filter(Boolean);
          const hasGhg = q.requires_ghg_table;
          const hasUpload = q.requires_file_upload;
          const qFiles = uploadedFiles[q.id] || [];

          return (
            <div key={q.id} data-testid={`question-card-${q.id}`}
              className={`bg-white border rounded-xl p-5 transition-colors ${selectedAnswer ? `${P.borderSoft} shadow-sm` : 'border-gray-200'}`}>
              <div className="flex items-start gap-3 mb-3">
                <span className="text-xs font-bold text-gray-400 bg-gray-100 rounded px-2 py-1 mt-0.5">{idx + 1}</span>
                <div className="flex-1">
                  <p className="text-gray-900 text-sm font-medium leading-relaxed">{q.question_text}</p>
                  {q.explanation && <p className="text-gray-500 text-xs mt-1.5 leading-relaxed">{q.explanation}</p>}
                  {q.esrs_reference && <span className="inline-block text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded mt-2">{q.esrs_reference}</span>}
                </div>
              </div>

              {/* Answer Options */}
              <div className="flex flex-wrap gap-2 ml-9">
                {opts.length > 0 ? opts.map(opt => (
                  <button key={opt.id} data-testid={`answer-${opt.id}-${q.id}`}
                    onClick={() => handleSelectAnswer(q.id, opt.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border ${selectedAnswer?.answer_option_id === opt.id
                      ? opt.is_na ? 'bg-gray-600 border-gray-500 text-white' : P.optionSelected
                      : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'}`}>
                    {opt.name}
                    {!opt.is_na && <span className="text-xs ml-1.5 opacity-60">({opt.points}pts)</span>}
                  </button>
                )) : <p className="text-xs text-gray-400 italic py-2">No answer options configured.</p>}
              </div>

              {/* Per-option conditional Comment & Document */}
              {(() => {
                const selOpt = opts.find(o => o.id === selectedAnswer?.answer_option_id);
                if (!selOpt) return null;
                const needComment = selOpt.comment_mode && selOpt.comment_mode !== "none";
                const needDoc = selOpt.document_mode && selOpt.document_mode !== "none";
                if (!needComment && !needDoc) return null;
                const vErr = validationErrors[q.id];
                return (
                  <div className={`ml-9 mt-3 space-y-2 ${P.softBg} border ${P.softBorder} rounded-lg p-3`} data-testid={`conditional-inputs-${q.id}`}>
                    {needComment && (
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Comment {selOpt.comment_mode === "required" && <span className="text-red-500">*</span>}
                        </label>
                        <textarea rows={2} value={comments[q.id] || ""}
                          onChange={e => setComments(prev => ({ ...prev, [q.id]: e.target.value }))}
                          data-testid={`comment-input-${q.id}`}
                          placeholder="Add a brief explanation..."
                          className={`w-full bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-900 outline-none focus:ring-1 ${P.ring} resize-none`} />
                      </div>
                    )}
                    {needDoc && (
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Supporting Document {selOpt.document_mode === "required" && <span className="text-red-500">*</span>}
                        </label>
                        <label className="inline-flex items-center gap-2 text-sm text-blue-700 bg-white border border-blue-200 px-3 py-1.5 rounded cursor-pointer hover:bg-blue-50 transition-colors">
                          <Upload className="w-3.5 h-3.5" />
                          <span>Choose file</span>
                          <input type="file" className="hidden" data-testid={`option-file-input-${q.id}`}
                            onChange={e => { if (e.target.files?.[0]) handleFileUpload(q.id, e.target.files[0]); }} />
                        </label>
                        {(uploadedFiles[q.id] || []).length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {(uploadedFiles[q.id] || []).map(f => (
                              <div key={f.id} className="flex items-center gap-2 bg-white border border-gray-200 rounded px-2.5 py-1.5 text-xs">
                                <Paperclip className="w-3 h-3 text-gray-400" />
                                <span className="text-gray-700">{f.filename}</span>
                                <button onClick={() => removeFile(q.id, f.id)} className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {vErr && <p className="text-xs text-red-600" data-testid={`validation-error-${q.id}`}>{vErr}</p>}
                  </div>
                );
              })()}

              {/* Linked Data Tables (GHG etc) */}
              {linkedTables.length > 0 && (
                <div className="ml-9 mt-3 space-y-2">
                  {linkedTables.map(table => (
                    <div key={table.id} className="border border-blue-200 rounded-lg overflow-hidden">
                      <button onClick={() => toggleTable(`${q.id}-${table.id}`)}
                        className="w-full flex items-center justify-between px-4 py-2.5 bg-blue-50 text-sm text-blue-700 hover:bg-blue-100 transition-colors">
                        <span className="flex items-center gap-2"><Table2 className="w-4 h-4" /> {table.name}</span>
                        {expandedTables[`${q.id}-${table.id}`] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      {expandedTables[`${q.id}-${table.id}`] && (
                        <div className="p-3 overflow-auto">
                          {table.description && <p className="text-xs text-gray-500 mb-2">{table.description}</p>}
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="bg-gray-50">
                                {(table.columns || []).map(col => (
                                  <th key={col.name} className="px-3 py-2 text-left text-gray-600 font-medium">{col.name} {col.unit && <span className="text-gray-400">({col.unit})</span>}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {(table.rows || []).map((row, ri) => (
                                <tr key={ri} className="border-t border-gray-100">
                                  {(table.columns || []).map(col => (
                                    <td key={col.name} className="px-3 py-2 text-gray-800">{row[col.name] ?? '-'}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Carbon Calculator for GHG questions */}
              {hasGhg && (
                <div className="ml-9 mt-3">
                  <button onClick={() => toggleTable(q.id)}
                    className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 px-4 py-2.5 rounded-lg hover:bg-emerald-100 transition-colors w-full justify-between border border-emerald-200">
                    <span className="flex items-center gap-2 font-medium"><Calculator className="w-4 h-4" /> Carbon Calculator</span>
                    {expandedTables[q.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {expandedTables[q.id] && (
                    <div className="mt-2">
                      <CarbonCalculator 
                        initialData={ghgData[q.id]}
                        onCalculated={(result) => {
                          setGhgData(prev => ({
                            ...prev,
                            [q.id]: {
                              scope1: { emissions: result.scope1 },
                              scope2: { emissions: result.scope2 },
                              scope3: { emissions: result.scope3 },
                              total: result.total,
                              breakdown: result
                            }
                          }));
                        }}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* File Upload */}
              {hasUpload && (
                <div className="ml-9 mt-3">
                  <label className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 px-3 py-2 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors">
                    <Upload className="w-4 h-4" />
                    <span>Upload Supporting Document</span>
                    <input type="file" className="hidden" onChange={e => {
                      if (e.target.files?.[0]) handleFileUpload(q.id, e.target.files[0]);
                    }} />
                  </label>
                  {qFiles.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {qFiles.map(f => (
                        <div key={f.id} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded px-2.5 py-1.5 text-xs">
                          <Paperclip className="w-3 h-3 text-gray-400" />
                          <span className="text-gray-700">{f.filename}</span>
                          <button onClick={() => removeFile(q.id, f.id)} className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-8 flex justify-between items-center flex-wrap gap-3">
        {totalAnswered < totalQuestions && (
          <p className="text-sm text-amber-600">
            Please answer all {totalQuestions} questions to submit ({totalQuestions - totalAnswered} remaining)
          </p>
        )}
        <div className="flex gap-3 ml-auto flex-wrap items-center">
          {draftSavedAt && (
            <span className="text-xs text-emerald-600" data-testid="draft-saved-indicator">
              Draft saved at {draftSavedAt.toLocaleTimeString()}
            </span>
          )}
          <button data-testid="save-draft-btn" onClick={handleSaveDraft} disabled={savingDraft || !assessmentId || totalAnswered === 0}
            className={`inline-flex items-center gap-2 border ${P.borderSoft} bg-white hover:${P.bgSoft} disabled:opacity-50 ${P.text} font-medium px-5 py-3 rounded-xl transition-colors`}>
            <Save className="w-4 h-4" />
            {savingDraft ? 'Saving...' : 'Save Draft'}
          </button>
          <button data-testid="save-and-exit-btn" onClick={handleSaveAndExit} disabled={savingDraft || !assessmentId || totalAnswered === 0}
            className="inline-flex items-center gap-2 border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 text-gray-700 font-medium px-5 py-3 rounded-xl transition-colors">
            Save & Exit
          </button>
          <button data-testid="submit-questionnaire-btn" onClick={handleSubmit} disabled={submitting || totalAnswered < totalQuestions}
            className={`${P.btnPrimary} disabled:opacity-50 text-white font-semibold px-8 py-3 rounded-xl transition-colors`}>
            {submitting ? 'Submitting...' : 'Continue to Review'}
          </button>
        </div>
      </div>
      {showRegisterModal && (
        <RegisterToSaveModal
          draftId={assessmentId}
          prefillEmail={initialAssessment?.company_info?.contact_email}
          prefillName={initialAssessment?.company_info?.contact_name}
          prefillCompany={initialAssessment?.company_info?.company_name}
          onClose={() => setShowRegisterModal(false)}
          onSaved={handleRegisterSaved}
        />
      )}
    </div>
  );
};

export default QuestionnaireStep;

